import { format as formatDate, isSameDay } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCampaignDetailDate,
  addCampaignDetailItem,
  getCampaignDetailItems,
  updateCampaignDetailItem,
} from "@/sideEffects/api";
import { CampaignDetails, DayOfWeek } from "@/lib/types";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { convertTimeToUtc } from "@/lib/dateUtils";
import { CampaignRobotItemAddForm } from "./CampaignRobotItemAddForm";
import { CampaignRobotItemUpdateForm } from "./CampaignRobotItemUpdateForm";

interface Props {
  campaignDetails: CampaignDetails;
}

export const CampaignRobotScheduleForm: React.FC<Props> = ({ campaignDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const campaignDetailItems = useQuery({
    queryKey: ["getCampaignDetailItems", campaignDetails.intId],
    queryFn: () => getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid)),
  });

  const campaignDetailItemsData = campaignDetailItems.data?.data.result || [];

  const queryClient = useQueryClient();
  const addEventMutation = useMutation({
    mutationFn: async (values: { start: Date; end: Date }) => {
      const weekDay = values.start.getDay() as DayOfWeek;
      const isOnlyOneDay = isSameDay(values.start, values.end);

      const campaignDetailItem = addCampaignDetailItem({
        campaignId: campaignDetails.intId,
        periodStart: formatDate(values.start, "yyyy-MM-dd"),
        periodEnd: formatDate(values.end, "yyyy-MM-dd"),
        isActive: true,
        firmId: String(activeFirm?.firmGuid),
      });

      if (!isOnlyOneDay) {
        return await campaignDetailItem;
      }

      const response = await campaignDetailItem;
      const campaignDetailItems = await getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid));

      const campaignDetailItemsData = campaignDetailItems.data.result || [];
      const newCampaignDetailItem = campaignDetailItemsData.find((item) => item.id === response.data.newDataId);

      await addCampaignDetailDate({
        campaignDetailId: Number(newCampaignDetailItem?.intId),
        dayOfWeek: weekDay,
        startTime: convertTimeToUtc(values.start.toISOString()),
        endTime: convertTimeToUtc(values.end.toISOString()),
        firmGuid: String(activeFirm?.firmGuid),
      });

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailItems"] });
      showSuccess(t("common.addedEventSuccess"));
    },
    onError: showApiError,
  });

  const updateEventMutation = useMutation({
    mutationFn: (values: { start: Date; end: Date; id: string }) => {
      const campaignDetailItem = campaignDetailItemsData.find((item) => item.id === values.id);
      if (!campaignDetailItem) return Promise.reject();

      return updateCampaignDetailItem({
        campaignDetailItemId: campaignDetailItem.id,
        campaignId: campaignDetails.intId,
        periodStart: formatDate(values.start, "yyyy-MM-dd"),
        periodEnd: formatDate(values.end, "yyyy-MM-dd"),
        isActive: campaignDetailItem.isActive,
        firmId: String(activeFirm?.firmGuid),
        contentGroupId: campaignDetailItem.contentGroupintId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailItems"] });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const isLoading = campaignDetailItems.isLoading || addEventMutation.isPending || updateEventMutation.isPending;

  if (isLoading) return <LoadingSmall />;

  if (campaignDetailItemsData.length > 0) {
    return <CampaignRobotItemUpdateForm item={campaignDetailItemsData[0]} />;
  }

  return <CampaignRobotItemAddForm />;
};
